import api from './client';

export interface FabricCapacity {
  id: string;
  displayName: string;
  sku: string;
  region: string;
  state: 'Active' | 'Paused' | 'Suspended' | 'Scaling' | 'Resuming' | 'Suspending' | 'Deleting' | 'Failed' | 'Invalid';
  workspaceCount: number | null;
}

export interface FabricCapacitiesResponse {
  capacities: FabricCapacity[];
  workspacesTotal: number | null;
  connectionName: string;
  isAdminAccess: boolean;
  workspaceDataUnavailable?: boolean;
  fetchedAt: string;
}

export interface FabricWorkspace {
  id: string;
  displayName: string;
  type: string;
  capacityId: string | null;
  capacityAssignmentProgress: string;
}

export interface FabricWorkload {
  name: string;
  state: 'Enabled' | 'Disabled' | 'Unsupported';
  maxMemoryPercent: number;
  maxMemoryPercentSetByUser: number | null;
}

export async function getFabricCapacities(connectionId?: string): Promise<FabricCapacitiesResponse> {
  const params = connectionId ? `?connectionId=${connectionId}` : '';
  const response = await api.get(`/api/fabric/capacities${params}`);
  return response.data;
}

export interface FabricActivityEvent {
  id: string;
  creationTime: string;
  operation: string;
  userId: string;
  workspaceId: string;
  workspaceName: string;
  itemId: string;
  itemName: string;
  itemType: string;
  userAgent: string;
  clientIP: string;
}

export interface FabricActivityResponse {
  events: FabricActivityEvent[];
  total: number;
  start: string;
  end: string;
  fetchedAt: string;
}

export async function getFabricActivityEvents(params: {
  startDate?: string;
  endDate?: string;
  workspaceId?: string;
  userId?: string;
  operation?: string;
  connectionId?: string;
}): Promise<FabricActivityResponse> {
  const qs = new URLSearchParams();
  if (params.startDate)    qs.set('startDate', params.startDate);
  if (params.endDate)      qs.set('endDate', params.endDate);
  if (params.workspaceId)  qs.set('workspaceId', params.workspaceId);
  if (params.userId)       qs.set('userId', params.userId);
  if (params.operation)    qs.set('operation', params.operation);
  if (params.connectionId) qs.set('connectionId', params.connectionId);
  const response = await api.get(`/api/fabric/audit/activities?${qs.toString()}`);
  return response.data;
}

export async function getFabricWorkspaces(connectionId?: string): Promise<{ workspaces: FabricWorkspace[]; total: number; connectionName: string; fetchedAt: string }> {
  const params = connectionId ? `?connectionId=${connectionId}` : '';
  const response = await api.get(`/api/fabric/workspaces${params}`);
  return response.data;
}

export async function getFabricCapacityWorkloads(
  capacityId: string,
  connectionId?: string
): Promise<{ capacityId: string; workloads: FabricWorkload[]; fetchedAt: string }> {
  const params = connectionId ? `?connectionId=${connectionId}` : '';
  const response = await api.get(`/api/fabric/capacities/${capacityId}/workloads${params}`);
  return response.data;
}

export interface FabricItem {
  id: string;
  displayName: string;
  type: string;
  description: string;
}

export async function getFabricWorkspaceItems(
  workspaceId: string,
  connectionId?: string
): Promise<{ items: FabricItem[]; total: number }> {
  const params = connectionId ? `?connectionId=${connectionId}` : '';
  const response = await api.get(`/api/fabric/workspaces/${workspaceId}/items${params}`);
  return response.data;
}

// ─── Runtime Monitoring ───────────────────────────────────────────────────────

export type RunStatus = 'Succeeded' | 'Failed' | 'Running' | 'Cancelled' | 'Deduped' | 'Pending';

export interface FabricRun {
  item_type: 'pipeline' | 'notebook';
  item_id: string;
  item_name: string;
  workspace_id: string;
  workspace_name: string;
  instance_id: string;
  status: RunStatus;
  started_at: string | null;
  ended_at: string | null;
  duration_seconds: number | null;
  error_message: string | null;
  retry_count: number | null;
  fetched_at: string;
}

export interface RuntimeStats {
  pipelineRuns: number;
  notebookRuns: number;
  pipelineFailures: number;
  notebookFailures: number;
  pipelineRunning: number;
  notebookRunning: number;
  avgPipelineDuration: number;
  avgNotebookDuration: number;
  lastSync: string | null;
}

export interface RuntimeRunsResponse {
  runs: FabricRun[];
  total: number;
  stats: RuntimeStats;
  fetchedAt: string;
}

export async function getFabricRuntimeRuns(params: {
  type?: 'pipeline' | 'notebook';
  workspaceId?: string;
  status?: string;
  limit?: number;
  offset?: number;
  refresh?: boolean;
  connectionId?: string;
}): Promise<RuntimeRunsResponse> {
  const qs = new URLSearchParams();
  if (params.type)         qs.set('type', params.type);
  if (params.workspaceId)  qs.set('workspaceId', params.workspaceId);
  if (params.status)       qs.set('status', params.status);
  if (params.limit)        qs.set('limit', String(params.limit));
  if (params.offset)       qs.set('offset', String(params.offset));
  if (params.refresh)      qs.set('refresh', 'true');
  if (params.connectionId) qs.set('connectionId', params.connectionId);
  const response = await api.get(`/api/fabric/runtime/runs?${qs.toString()}`);
  return response.data;
}

export async function getFabricRuntimeWorkspaces(): Promise<{ workspaces: { workspace_id: string; workspace_name: string }[] }> {
  const response = await api.get('/api/fabric/runtime/workspaces');
  return response.data;
}

export async function triggerFabricRuntimeSync(connectionId?: string): Promise<{ success: boolean; runs: number; syncedAt: string }> {
  const params = connectionId ? `?connectionId=${connectionId}` : '';
  const response = await api.post(`/api/fabric/runtime/sync${params}`);
  return response.data;
}

// ─── Drift Analysis ───────────────────────────────────────────────────────────

export type DriftStatus = 'Matched' | 'Missing' | 'Untracked';

export interface DriftItem {
  canvasName: string;
  type: string;
  status: DriftStatus;
}

export interface DriftWorkspace {
  canvasId: string | null;
  canvasName: string | null;
  liveWsId: string | null;
  liveWsName: string | null;
  status: DriftStatus;
  items: DriftItem[];
}

export interface DriftArchitecture {
  archId: string;
  archName: string;
  workspaces: DriftWorkspace[];
}

export interface DriftRecommendation {
  type: 'warning' | 'info';
  rule: string;
  archName: string;
  message: string;
}

export interface DriftMetrics {
  architecturesAnalyzed: number;
  liveWorkspacesVisible: number;
  workspacesInCanvas: number;
  workspacesMatched: number;
  workspacesMissing: number;
  itemsMatched: number;
  itemsMissing: number;
  healthScore: number;
  healthStatus: 'Healthy' | 'Warning' | 'Critical';
}

export interface DriftAnalysisResponse {
  architectures: DriftArchitecture[];
  untrackedWorkspaces: DriftWorkspace[];
  metrics: DriftMetrics;
  recommendations: DriftRecommendation[];
  fetchedAt: string;
}

export async function getFabricDrift(connectionId?: string): Promise<DriftAnalysisResponse> {
  const params = connectionId ? `?connectionId=${connectionId}` : '';
  const response = await api.get(`/api/fabric/drift${params}`);
  return response.data;
}

export async function pauseFabricCapacity(capacityId: string, connectionId?: string): Promise<{ success: boolean; message: string }> {
  const params = connectionId ? `?connectionId=${connectionId}` : '';
  const response = await api.post(`/api/fabric/capacities/${capacityId}/pause${params}`);
  return response.data;
}

export async function resumeFabricCapacity(capacityId: string, connectionId?: string): Promise<{ success: boolean; message: string }> {
  const params = connectionId ? `?connectionId=${connectionId}` : '';
  const response = await api.post(`/api/fabric/capacities/${capacityId}/resume${params}`);
  return response.data;
}
